"use client"

import { useRef, useEffect, useState } from "react"
import Link from "next/link"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      // Header slide in
      if (headerRef.current) {
        gsap.from(headerRef.current, {
          x: -60,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        })
      }

      // CTA fade in
      if (ctaRef.current) {
        gsap.from(ctaRef.current, {
          y: 30,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        })
      }

    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative py-32 pl-6 md:pl-28 pr-6 md:pr-12 border-t border-border/30"
    >
      {/* Expandable description */}
      <div className="mb-16">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="group inline-flex items-center gap-3 min-h-[44px] border border-foreground/20 px-6 py-3 font-mono text-sm uppercase tracking-widest text-foreground hover:border-accent hover:text-accent transition-all duration-200"
          aria-expanded={isExpanded}
          aria-controls="about-expanded-content"
          id="about-see-more"
        >
          {isExpanded ? "See less" : "See more"}
          <svg
            className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isExpanded && (
          <div id="about-expanded-content" className="mt-14 max-w-5xl space-y-16" aria-labelledby="about-see-more">
            {/* Intro block */}
            <div className="space-y-6">
              <h3 className="font-[var(--font-bebas)] text-4xl md:text-5xl tracking-tight text-foreground">
                ChillarOps – Cybersecurity Engineering Company
              </h3>
              <div className="space-y-4 font-sans text-base md:text-lg text-foreground/90 leading-[1.7] max-w-3xl">
                <p>
                  ChillarOps is a cybersecurity engineering company specializing in offensive security, 
                  cloud infrastructure, DevSecOps, networking, and secure software engineering.

We help startups, enterprises, and growing businesses build, secure, and scale 
modern digital infrastructure.

Our team brings deep expertise in:
- Offensive & defensive security
- Cloud architecture (AWS, Azure, GCP)
- DevSecOps and infrastructure security
- Network security and zero-trust
- Incident response and threat intelligence
- Active security research

Cybersecurity is our identity. Software development is one of our capabilities.
                </p>
              </div>
            </div>

            {/* What We Do - Grid layout */}
            <div className="space-y-8">
              <h4 className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Core Capabilities</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="border border-border/50 p-6 space-y-3">
                  <h5 className="font-mono text-sm font-semibold text-foreground">1. Offensive Security</h5>
                  <ul className="space-y-2 font-sans text-sm text-muted-foreground leading-relaxed">
                    <li>· Penetration testing & vulnerability assessment</li>
                    <li>· Red team operations & incident simulation</li>
                    <li>· Exploit development & malware analysis</li>
                    <li>· Security architecture review & hardening</li>
                  </ul>
                </div>
                <div className="border border-border/50 p-6 space-y-3">
                  <h5 className="font-mono text-sm font-semibold text-foreground">2. DevSecOps Engineering</h5>
                  <ul className="space-y-2 font-sans text-sm text-muted-foreground leading-relaxed">
                    <li>· Secure CI/CD pipeline implementation</li>
                    <li>· Infrastructure as Code security</li>
                    <li>· Container security & orchestration</li>
                    <li>· Secret management & compliance</li>
                  </ul>
                </div>
                <div className="border border-border/50 p-6 space-y-3">
                  <h5 className="font-mono text-sm font-semibold text-foreground">3. Cloud Security</h5>
                  <ul className="space-y-2 font-sans text-sm text-muted-foreground leading-relaxed">
                    <li>· AWS, Azure, GCP security architecture</li>
                    <li>· Identity & access management (IAM)</li>
                    <li>· Network security & zero-trust</li>
                    <li>· Compliance & regulatory requirements</li>
                  </ul>
                </div>
                <div className="border border-border/50 p-6 space-y-3">
                  <h5 className="font-mono text-sm font-semibold text-foreground">4. Incident Response</h5>
                  <ul className="space-y-2 font-sans text-sm text-muted-foreground leading-relaxed">
                    <li>· 24/7 monitoring & threat detection</li>
                    <li>· Rapid incident containment</li>
                    <li>· Forensic analysis & recovery</li>
                    <li>· Post-incident strengthening</li>
                  </ul>
                </div>
                <div className="border border-border/50 p-6 space-y-3">
                  <h5 className="font-mono text-sm font-semibold text-foreground">5. Security Research</h5>
                  <ul className="space-y-2 font-sans text-sm text-muted-foreground leading-relaxed">
                    <li>· Vulnerability discovery & disclosure</li>
                    <li>· Threat intelligence & trend analysis</li>
                    <li>· Security advisories & publications</li>
                    <li>· Training & awareness programs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Get in touch CTA */}
      <div
        ref={ctaRef}
        className="mt-24 pt-16 border-t border-border/30"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">05 / Contact</span>
        <h3 className="mt-4 font-[var(--font-bebas)] text-4xl md:text-5xl tracking-tight mb-8">
          GET IN TOUCH
        </h3>
        <p className="max-w-xl font-sans text-base text-muted-foreground mb-8 leading-relaxed">
          Have a project in mind or want to collaborate? We&apos;d love to hear from you.
        </p>
        <Link
          href="/contact"
          className="group inline-flex items-center gap-3 min-h-[44px] border border-foreground/20 px-8 py-3 font-mono text-sm uppercase tracking-widest text-foreground hover:border-accent hover:text-accent transition-all duration-200"
          aria-label="Get in touch - contact us"
        >
          Get in Touch
          <svg
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>

    </section>
  )
}
